import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/app_components/ui/form';
import { Input } from '@/app_components/ui/input';
import * as Select from '@/app_components/ui/select';
import {
  maxTermLengths,
  minTermLengths,
  type RatesInfoInputKey,
  type RatesInfoInputValue,
  savingsAccountTypes,
} from '@/app_data/widgets/RedVentures/constants';
import { FilterBy } from '@/app_data/widgets/utils';
import { cn, defaultZipCode } from '@/app_lib/utils/utils';

interface RatesInputProps {
  inputs: RatesInfoInputKey[];
  onChange: (data: FilterBy) => void;
}

export const RatesInput = ({ inputs, onChange }: RatesInputProps) => {
  const form = useForm<FilterBy>({
    defaultValues: {
      accountType: 'MMA,SAVINGS',
      depositAmount: 25000,
      maxTermLength: 36,
      minTermLength: 6,
      zip: defaultZipCode,
    },
    mode: 'onChange',
    resolver: zodResolver(FilterBy),
  });

  const {
    formState: { errors },
  } = form;

  const depositInput = useMemo(
    () => (
      <FormField
        control={form.control}
        name='depositAmount'
        render={({ field, formState }) => (
          <FormItem
            className='flex flex-col gap-2 w-[calc(50%-16px)] flex-grow'
            data-cy='depositAmount'
          >
            <FormLabel
              className='typography-body-graphik'
              htmlFor='depositAmount'
            >
              Deposit Amount
            </FormLabel>
            <FormControl>
              <div>
                <Input
                  className='p-4 pl-8 shrink-0 self-stretch w-full placeholder:self-center'
                  id='depositAmount'
                  min={0}
                  placeholder='Deposit Amount'
                  step={1000}
                  type='number'
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && !Object.keys(formState.errors).length) {
                      onChange(form.getValues());
                    }
                  }}
                />
                <span className='relative left-4 bottom-[31px]'>$</span>
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    ),
    [form, onChange],
  );

  const zipInput = useMemo(
    () => (
      <FormField
        control={form.control}
        name='zip'
        render={({ field, formState }) => (
          <FormItem
            className='flex flex-col w-[calc(50%-16px)] flex-grow space-y-0'
            data-cy='zipCode'
          >
            <FormLabel
              className={cn('typography-body-graphik', formState.errors.zip && 'border-red-500')}
              htmlFor='zip'
            >
              Zip Code
            </FormLabel>
            <FormControl>
              <Input
                className='p-4 mt-4 shrink-0 self-stretch w-full placeholder:self-center'
                placeholder='Enter the zip code'
                {...field}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' && !Object.keys(formState.errors).length) {
                    onChange(form.getValues());
                  }
                }}
              />
            </FormControl>
            <p className='text-red-500 typography-body-graphik-xsmall font-graphik-condensed'>{errors.zip?.message}</p>
          </FormItem>
        )}
      />
    ),
    [errors.zip, form, onChange],
  );

  const minTermInput = useMemo(
    () => (
      <FormField
        control={form.control}
        name='minTermLength'
        render={({ field }) => (
          <FormItem
            className='flex flex-col gap-2 w-[calc(50%-16px)] flex-grow'
            data-cy='minTermLength'
          >
            <FormLabel
              className='typography-body-graphik'
              htmlFor='minTermLength'
            >
              Min. Term Length
            </FormLabel>
            <FormControl>
              <Select.Root
                {...field}
                onValueChange={(value) => {
                  field.onChange(Number(value));
                  onChange(form.getValues());
                }}
                value={field.value?.toString()}
              >
                <Select.Trigger className='h-10 p-4 shrink-0 self-stretch w-full'>
                  <Select.Value placeholder='Min term length' />
                </Select.Trigger>
                <Select.Content>
                  {minTermLengths.map((item) => (
                    <Select.Item
                      key={item.value}
                      value={item.value}
                    >
                      {item.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormControl>
          </FormItem>
        )}
      />
    ),
    [form, onChange],
  );

  const minTermLength = form.watch('minTermLength');

  const maxTermInput = useMemo(
    () => (
      <FormField
        control={form.control}
        name='maxTermLength'
        render={({ field }) => (
          <FormItem
            className='flex flex-col gap-2 w-[calc(50%-16px)] flex-grow'
            data-cy='maxTermLength'
          >
            <FormLabel
              className='typography-body-graphik'
              htmlFor='maxTermLength'
            >
              Max. Term Length
            </FormLabel>
            <FormControl>
              <Select.Root
                {...field}
                onValueChange={(value) => {
                  field.onChange(Number(value));
                  onChange(form.getValues());
                }}
                value={field.value?.toString()}
              >
                <Select.Trigger className='h-10 p-4 shrink-0 self-stretch w-full'>
                  <Select.Value placeholder='Max term length' />
                </Select.Trigger>
                <Select.Content>
                  {maxTermLengths(minTermLength).map((item) => (
                    <Select.Item
                      key={item.value}
                      value={item.value}
                    >
                      {item.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormControl>
          </FormItem>
        )}
      />
    ),
    [form, minTermLength, onChange],
  );

  const accountTypeInput = useMemo(
    () => (
      <FormField
        control={form.control}
        name='accountType'
        render={({ field }) => (
          <FormItem className='flex flex-col gap-2 w-[calc(50%-16px)] flex-grow'>
            <FormLabel
              className='typography-body-graphik'
              htmlFor='accountType'
            >
              Account Type
            </FormLabel>
            <FormControl>
              <Select.Root
                {...field}
                onValueChange={(value) => {
                  field.onChange(value);
                  onChange(form.getValues());
                }}
                value={field.value?.toString()}
              >
                <Select.Trigger className='h-10 p-4 shrink-0 self-stretch w-full'>
                  <Select.Value placeholder='Account Type' />
                </Select.Trigger>
                <Select.Content>
                  {savingsAccountTypes.map((item) => (
                    <Select.Item
                      key={item.value}
                      value={item.value}
                    >
                      {item.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormControl>
          </FormItem>
        )}
      />
    ),
    [form, onChange],
  );

  const RatesInfoInputs: Record<RatesInfoInputKey, RatesInfoInputValue> = {
    accountType: {
      input: accountTypeInput,
      label: 'Account Type',
    },
    depositAmount: {
      input: depositInput,
      label: 'Deposit Amount',
    },
    maxTermLength: {
      input: maxTermInput,
      label: 'Max. Term Length',
    },
    minTermLength: {
      input: minTermInput,
      label: 'Min. Term Length',
    },
    zip: {
      input: zipInput,
      label: 'Zip Code',
    },
  };

  return (
    <Form {...form}>
      {/** biome-ignore lint/nursery/noRestrictedElements: Come back to this */}
      <form
        className='flex flex-wrap items-start w-full self-stretch gap-4 py-0 px-4 font-graphik-condensed'
        id='cd-rates'
      >
        {inputs.map((inputForm) => {
          const input = RatesInfoInputs[inputForm].input;
          return (
            <div
              className='contents'
              key={`rates-input-${inputForm}`}
            >
              {input}
            </div>
          );
        })}
      </form>
    </Form>
  );
};
